import { useState } from 'react';
import { Avatar } from '@/components/ui/avatar';
import { CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import {
  DROPDOWN_ROW_HEIGHT_PX,
  SearchableDropdown,
  type SearchableDropdownProps,
} from '@/components/ui/searchable-dropdown';
import { FieldTrigger } from '@/components/ui/field-trigger';
import { ChevronDownIcon } from '@/react/components/icons/ChevronDownIcon';
import { cn } from '@/lib/utils';
import { useTranslation } from '@hooks/useTranslation';
import type { Salesperson } from '@api/orders';

interface SalespersonSelectorProps {
  salespersons: Salesperson[];
  selectedId: number | undefined;
  onSelect: (id: number) => void;
  triggerClassName?: string;
  collisionPadding?: SearchableDropdownProps['collisionPadding'];
  invalid?: boolean;
}

export function SalespersonSelector({
  salespersons,
  selectedId,
  onSelect,
  triggerClassName = "w-[212px] h-16 px-4 py-0",
  collisionPadding,
  invalid = false,
}: SalespersonSelectorProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const selectedSalesperson = salespersons.find((salesperson) => salesperson.id === selectedId);
  const labelColorClass = selectedSalesperson ? 'text-off-black' : 'text-grey-light';

  return (
    <SearchableDropdown
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      searchPlaceholder={t('shared.search.salesperson')}
      selectedValue={selectedSalesperson ? String(selectedSalesperson.id) : undefined}
      collisionPadding={collisionPadding}
    >
      <SearchableDropdown.Trigger>
        <FieldTrigger
          role="combobox"
          aria-expanded={isOpen}
          aria-invalid={invalid || undefined}
          className={cn(
            'flex items-center justify-between gap-3',
            invalid && 'bg-chilli-blush border-chilli-red-light hover:border-chilli-red-light',
            triggerClassName,
          )}
          aria-label={selectedSalesperson ? `Selected salesperson: ${selectedSalesperson.name}` : 'Select a salesperson'}
        >
          {selectedSalesperson ? (
            <span className="flex min-w-0 items-center gap-3">
              <Avatar size="lg" src={selectedSalesperson.avatar_url ?? undefined} name={selectedSalesperson.name} />
              <span className={cn('truncate text-sm font-semibold', labelColorClass)}>
                {selectedSalesperson.name}
              </span>
            </span>
          ) : (
            <span className={cn('truncate text-sm', labelColorClass)}>
              {t('shared.select_salesperson')}
            </span>
          )}
          <ChevronDownIcon
            aria-hidden="true"
            className={cn(
              'shrink-0 transition-transform',
              invalid ? 'text-chilli-red' : 'text-off-black',
              isOpen && 'rotate-180',
            )}
          />
        </FieldTrigger>
      </SearchableDropdown.Trigger>

      <SearchableDropdown.List availableHeightOffset={DROPDOWN_ROW_HEIGHT_PX}>
        <CommandEmpty className="px-4 py-3 text-left text-sm text-grey">
          {t('shared.empty.no_salespersons')}
        </CommandEmpty>

        <CommandGroup className="overflow-visible p-0">
          <div className="flex flex-col gap-2 py-2">
            {salespersons.map((salesperson) => {
              const isPersonSelected = salesperson.id === selectedId;
              return (
                <CommandItem
                  key={salesperson.id}
                  value={String(salesperson.id)}
                  keywords={[salesperson.name]}
                  onSelect={() => {
                    onSelect(salesperson.id);
                    setIsOpen(false);
                  }}
                  className={cn(
                    "flex w-full cursor-pointer items-center gap-3 rounded-input py-2 pl-2 pr-4 text-sm font-semibold text-off-black leading-normal transition-colors hover:bg-dinnerplate-white-light data-[selected=true]:bg-dinnerplate-white-light",
                    isPersonSelected && "bg-dinnerplate-white-light",
                  )}
                >
                  <Avatar size="lg" src={salesperson.avatar_url ?? undefined} name={salesperson.name} />
                  <span>{salesperson.name}</span>
                </CommandItem>
              );
            })}
          </div>
        </CommandGroup>
      </SearchableDropdown.List>
    </SearchableDropdown>
  );
}
